import { useState, useEffect } from 'react';
import differenceInDays from 'date-fns/differenceInDays';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { useSessionData, useBooleanFeatureFlag, AccessStatus } from '@confluence/session-data';
import { Cohorts, COHORT_KEY } from '@confluence/onboarding-helpers/entry-points/constants/cohorts';
import {
	ONBOARDING_EXPERIENCES,
	ONBOARDING_TRACKER_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useOnboardingEligible } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingEligible';
import { useSitePersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useSitePersonalizationQuery';
import { useUserByLicensedProducts } from '@confluence/onboarding-helpers/entry-points/hooks/useUserByLicensedProducts';
import {
	useGetOnboardingState,
	deserializeState,
	useOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

const ONBOARDING_TRACKER_CROSSFLOW_EXPERIMENT = 'cc_onboarding_tracker_crossflow';
const ONBOARDING_TRACKER_LAND_EXPERIMENT = 'cc_onboarding_tracker_for_land';
const ACTIVATION_DATE = 'confluence.ondemand.activationDate';
const ONBOARDING_TRACKER_ELIGIBLE_DAYS = 30;

/**
 * Eligiblity check
 * 1. onboarding FF (confluence.frontend.onboarding.experience) is true
 * 2. user is new (eligibleNewUser) and have NOT done onboarding before (isNotEstablishedUser)
 * 3. user is a licensed admin
 * 4. user is a founding admin
 * 5. xflow users with xflow experiment flag or non-xflow users with land experiment flag
 * 6. user is within 30 days of activation
 *
 * Results
 * 1. isUserEligibleForOnboardingTracker = user pass above eligibility check for onboarding tracker experience
 * 2. Fire experiement exposure for all eligible users
 * 3. To tag new user in experiement -
 *    a. isUserEligibleForOnboardingTracker
 *    b. user is in experiment group (land user with land FF set as experiment or non-land user with crossflow experiment flag set as experiment)
 *    c. user is never tagged before
 * Mark isUserEligibleForOnboardingTrackerTagging == true
 *
 * @returns isOnboardingTrackerExperimentEligible - true | false
 * users pass check and are in onboarding tracker experiment group
 * 1. New user : isUserEligibleForOnboardingTrackerTagging
 *    (user pass Eligiblity check above and is about to be tagged)
 * 2. Existing user : isTaggedForOnboardingTrackerExperiment
 *    (user is tagged and has seen onboarding tracker before)
 */
export const useOnboardingTrackerEligible = () => {
	const { accessStatus, isLoggedIn, cloudId } = useSessionData();
	const { setOnboardingState } = useOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isUserEligibleForOnboardingTrackerTagging, setisUserEligibleForOnboardingTrackerTagging] =
		useState(false);

	const xflowCohort = FeatureGates.getExperimentValue<Cohorts>(
		ONBOARDING_TRACKER_CROSSFLOW_EXPERIMENT,
		COHORT_KEY,
		Cohorts.NOT_ENROLLED,
		{
			fireExperimentExposure: false,
		},
	);

	const landCohort = FeatureGates.getExperimentValue<Cohorts>(
		ONBOARDING_TRACKER_LAND_EXPERIMENT,
		COHORT_KEY,
		Cohorts.NOT_ENROLLED,
		{
			fireExperimentExposure: false,
		},
	);

	//1. onboarding FF
	const isOnboardingEligible = useBooleanFeatureFlag('confluence.frontend.onboarding.experience');
	//2. user is new
	const { isEstablishedUser, isEstablishedUserReady, eligibleNewUser } = useOnboardingEligible();
	const isNotEstablishedUser = isEstablishedUserReady && !isEstablishedUser;
	const isEligibleNewUserForOnboarding = eligibleNewUser || isNotEstablishedUser;

	//3. user is a licensed admin
	const isLicensedAdmin = isLoggedIn && accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;

	/**
	 * skip = true when both land/xflow experiment flag is not enrolled
	 */
	const isFFNotEnrolled = (currentFFValue: string) => currentFFValue === Cohorts.NOT_ENROLLED;
	const skipConditionsForQueries =
		!isOnboardingEligible || !isLicensedAdmin || [xflowCohort, landCohort].every(isFFNotEnrolled);

	//4. user is a founding admin
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values({
			...ONBOARDING_TRACKER_STATE_KEYS,
			...ONBOARDING_EXPERIENCES,
		}),
		skipConditionsForQueries,
	);

	const { isFoundingAdmin, isTaggedForOnboardingTrackerExperiment, isTrackerDismissed } =
		deserializeState(onboardingStateData);

	//5. land users belong to land experiment; xflow users belong to crossflow experiment
	const {
		isLandUser,
		loading: isLandUserLoading,
		error: isLandUserError,
	} = useUserByLicensedProducts({
		skip: skipConditionsForQueries || !isFoundingAdmin,
	});

	//6. activation date is within eligible period
	const {
		loading: sitePersonalizationQueryLoading,
		error: sitePersonalizationQueryError,
		attributes,
	} = useSitePersonalizationQuery({
		cloudId,
		skip: skipConditionsForQueries,
	});

	// Processing site personalization data
	const sitePersonalizationDataAttributes = attributes?.reduce<Record<string, string>>(
		(obj, item) => ({ ...obj, [item.name]: item.value }),
		{},
	);

	const activationDate: string | undefined = sitePersonalizationDataAttributes?.[ACTIVATION_DATE];

	// <= 30 days
	const isActivationDateEligible: boolean = !!(
		activationDate &&
		differenceInDays(new Date(), new Date(activationDate)) <= ONBOARDING_TRACKER_ELIGIBLE_DAYS
	);

	useEffect(() => {
		if (
			// queries have finished loading
			!isLandUserLoading &&
			!isLandUserError &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!sitePersonalizationQueryLoading &&
			!sitePersonalizationQueryError
		) {
			const isUserTrackerExperimentEligible =
				!skipConditionsForQueries &&
				isEligibleNewUserForOnboarding &&
				isFoundingAdmin &&
				isActivationDateEligible;

			if (isUserTrackerExperimentEligible) {
				if (isLandUser) {
					//Fire event exposure
					FeatureGates.manuallyLogExperimentExposure(ONBOARDING_TRACKER_LAND_EXPERIMENT);
					if ((landCohort as Cohorts) !== Cohorts.NOT_ENROLLED) {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'feature',
								attributes: {
									flagKey: ONBOARDING_TRACKER_LAND_EXPERIMENT,
									landCohort,
								},
							},
						}).fire();
					}
				} else {
					//Fire event exposure
					FeatureGates.manuallyLogExperimentExposure(ONBOARDING_TRACKER_CROSSFLOW_EXPERIMENT);
					if ((xflowCohort as Cohorts) !== Cohorts.NOT_ENROLLED) {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'exposed',
								actionSubject: 'feature',
								attributes: {
									flagKey: ONBOARDING_TRACKER_CROSSFLOW_EXPERIMENT,
									xflowCohort,
								},
							},
						}).fire();
					}
				}
				const isTrackerExperimentUser = isLandUser
					? landCohort === Cohorts.EXPERIMENT
					: xflowCohort === Cohorts.EXPERIMENT;
				if (isTrackerExperimentUser && !isTaggedForOnboardingTrackerExperiment) {
					// newly tagged user for the experiment
					setisUserEligibleForOnboardingTrackerTagging(true);

					void setOnboardingState({
						key: ONBOARDING_TRACKER_STATE_KEYS.EXPERIMENT_ELIGIBLE,
						value: String(true),
					});
				}
			}
		}
	}, [
		isLandUserLoading,
		isLandUserError,
		onboardingStateError,
		onboardingStateLoading,
		isFoundingAdmin,
		isLandUser,
		skipConditionsForQueries,
		isEligibleNewUserForOnboarding,
		isTaggedForOnboardingTrackerExperiment,
		setOnboardingState,
		createAnalyticsEvent,
		xflowCohort,
		landCohort,
		sitePersonalizationQueryLoading,
		sitePersonalizationQueryError,
		isActivationDateEligible,
	]);

	const isOnboardingTrackerExperimentEligible =
		(isUserEligibleForOnboardingTrackerTagging || isTaggedForOnboardingTrackerExperiment) &&
		(landCohort === Cohorts.EXPERIMENT || xflowCohort === Cohorts.EXPERIMENT);

	return {
		isOnboardingTrackerExperimentEligible,
		isTrackerDismissed,
		loading: isLandUserLoading || onboardingStateLoading || sitePersonalizationQueryLoading,
		error: isLandUserError || onboardingStateError || sitePersonalizationQueryError,
	};
};
